import {
  h,
  FunctionComponent,
  toChildArray,
  cloneElement,
  VNode,
} from 'preact';
import ReactModal from 'react-modal';
import { useEffect, useRef } from 'preact/hooks';
import Header from './components/Header';
import { MODAL_SIZES } from './constants';
import { ModalContent, ModalDialog, ModalOverlay, Body } from './Modal.styled';

interface ModalProps {
  className?: string;
  size?: MODAL_SIZES;
  isOpen: boolean;
  onClose?: () => void;
  closable?: boolean;
  aria?: {
    labelledby?: string;
    describedby?: string;
  };
}

interface ModalSubComponents {
  Body?: typeof Body;
  Header?: typeof Header;
}

const Modal: FunctionComponent<ModalProps> & ModalSubComponents = (
  props,
): JSX.Element => {
  const {
    className,
    size = MODAL_SIZES.md,
    children,
    onClose,
    isOpen,
    closable = true,
    aria,
  } = props;

  const shouldBlockModalCloseRef = useRef(false);

  // Handle ESC key behavior for expanded elements
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      // Prevent browser's default ESC behavior (exiting fullscreen)
      event.preventDefault();

      const { activeElement } = document;
      if (!activeElement) return;

      // If an element is expanded, let it handle ESC first
      const isExpanded = activeElement.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        shouldBlockModalCloseRef.current = true;
      }
    };

    // Use capture phase to handle event before it bubbles
    document.addEventListener('keydown', handleKeyDown, true);
    // eslint-disable-next-line consistent-return
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  const handleRequestClose = (event?: KeyboardEvent) => {
    // Handle non-ESC close events (clicking overlay)
    if (!event || event.key !== 'Escape') {
      onClose?.();
      return;
    }

    if (shouldBlockModalCloseRef.current) {
      event.stopPropagation();
      shouldBlockModalCloseRef.current = false; // Reset for next ESC press
      return;
    }

    onClose?.();
  };

  const addOnCloseCallbackToHeader = (modalChildren, onClose, closable) =>
    toChildArray(modalChildren).map((child: VNode) => {
      if (child.type === Header) {
        return cloneElement(child, {
          onClose,
          closable,
        });
      }
      return child;
    });

  const renderModalOverlay = (props, contentElement) => (
    <ModalOverlay {...props} style={{}}>
      {contentElement}
    </ModalOverlay>
  );

  const renderModalContent = (props, children) => (
    <ModalDialog size={size}>
      <ModalContent {...props} style={{}}>
        {children}
      </ModalContent>
    </ModalDialog>
  );

  const getParentDOM = () => {
    const portalWrapper = document.querySelector('#portalWrapper');
    return portalWrapper || document.body;
  };

  return (
    <ReactModal
      className={className}
      onRequestClose={handleRequestClose}
      isOpen={isOpen}
      parentSelector={() => getParentDOM()}
      ariaHideApp={false}
      overlayElement={renderModalOverlay}
      contentElement={renderModalContent}
      aria={aria}
    >
      {addOnCloseCallbackToHeader(children, onClose, closable)}
    </ReactModal>
  );
};

Modal.Body = Body;
Modal.Header = Header;

export { Modal };
